import type { IOgGridTableProps } from '@opengov/components-og-grid-table';
import type { Column } from 'ag-grid-community';
import { selectIsQueryRunning } from '@opengov/ogql-client/selectors';
import { graphToSummaryGridRowData, rawToSorted } from '@opengov/reporting-transforms/summaryTable';
import { EQueryType, EReportColumnTypes, SortDirection } from '@opengov/reporting-types';
import { createSelector } from 'reselect';

import { selectReportExplorerMsgs } from '../../../common/locale';
import { selectCurrentReport } from '../../../common/report/selectors/reportSelectors';
import {
  selectIsInStandardReport,
  selectPresentationConfiguration
} from '../../../common/reportConfiguration/selectors';
import { selectCurrentTemplate } from '../../../common/reportTemplate/standardTemplateSelectors';
import { selectSummaryTableData, selectSummaryTableReportConfiguration } from '../../../summaryTable/selectors';
import { selectColumnDisplayName } from '../../selectors/selectReportExplorerState';
import { reportDataTableAgGridActionCreators } from '../state';

import { selectAgGridColumnTypes } from './selectAgGridColumnTypes';
import { selectSummaryGridColumnDefs } from './selectSummaryGridColumnDefs';

import { reportConfigurationActionCreators } from '../../../common/reportConfiguration/state';
import { reportConfigurationEffects } from '../../../common/reportConfiguration/effects';

const summaryGridOptions = (
  autoHeaderName: string,
  sortColumnName: string | null,
  sortDirection: SortDirection
): IOgGridTableProps['gridOptions'] => {
  return {
    treeData: true,
    groupDefaultExpanded: -1,
    initialState: {
      sort: sortColumnName
        ? {
            sortModel: [
              {
                colId: sortColumnName,
                sort: sortDirection === SortDirection.ASC ? 'asc' : 'desc'
              }
            ]
          }
        : undefined
    },
    autoGroupColumnDef: {
      headerName: autoHeaderName,
      cellRendererParams: { suppressCount: true },
      lockPinned: true,
      pinned: 'left'
    },
    suppressDragLeaveHidesColumns: true,
    defaultColDef: {
      cellRendererSelector: undefined,
      resizable: true,
      suppressHeaderMenuButton: true
    },
    getDataPath: (data): string[] => data.name, // it's already a string[]
    loadingOverlayComponent: 'CustomLoadingOverlay',
    suppressContextMenu: true
  };
};

export const selectSummaryAgGridProps = createSelector(
  selectSummaryGridColumnDefs,
  selectSummaryTableData,
  selectCurrentReport,
  selectSummaryTableReportConfiguration,
  selectAgGridColumnTypes,
  selectIsQueryRunning,
  selectReportExplorerMsgs,
  selectColumnDisplayName,
  selectIsInStandardReport,
  selectCurrentTemplate,
  selectPresentationConfiguration,
  (
    columnDefs,
    chartData,
    report,
    reportConfiguration,
    columnTypes,
    isQueryRunning,
    explorerMsgs,
    findColumnDisplayName,
    isInStandardReport,
    currentTemplate,
    presentation
  ): IOgGridTableProps => {
    // it needs to not be the default empty record
    if (
      !reportConfiguration ||
      (isInStandardReport && !currentTemplate) ||
      (!reportConfiguration.viewId && report && !report.lensId)
    ) {
      return {
        loading: true,
        columnDefs,
        rowData: [],
        gridOptions: {
          ...summaryGridOptions('', null, SortDirection.None),
          ...{ columnTypes }
        }
      };
    }

    const categorizedByColumn = reportConfiguration.visualizationQuery.categorizedByColumn;
    const showName = reportConfiguration.visualizationQuery.showColumn.displayName;
    const isPercentOfTotal =
      reportConfiguration.visualizationQuery.showColumn.columnType === EReportColumnTypes.percentOfTotal;
    const { sortColumnName, sortDirection } = presentation.summary;
    const rawRowData = graphToSummaryGridRowData(chartData, showName, isPercentOfTotal, explorerMsgs.emptyValues);
    const rowData = rawToSorted(rawRowData, sortColumnName, SortDirection[sortDirection]);
    const categorizedByColumnLabel = findColumnDisplayName(categorizedByColumn);
    const sumGridOptions = summaryGridOptions(categorizedByColumnLabel, sortColumnName, SortDirection[sortDirection]);

    return {
      alwaysShowHorizontalScroll: true,
      alwaysShowVerticalScroll: true,
      loading: isQueryRunning(EQueryType.summaryTableQuery) || isQueryRunning(EQueryType.visualizationQuery),
      columnDefs,
      rowData,
      gridOptions: { ...sumGridOptions, ...{ columnTypes } },
      onColumnHeaderClicked: (event) => {
        const columnName = (event.column as Column).getColId();
        reportDataTableAgGridActionCreators.setActiveColumn(columnName);
      },
      onSortChanged: (event) => {
        const sortState = event.api.getColumnState().find((s) => s.sort != null);
        const sortColumnName = sortState?.colId ?? '';
        const sortDirection = (sortState?.sort?.toLocaleUpperCase() ?? SortDirection.None) as SortDirection;

        if (
          presentation.summary.sortColumnName !== sortColumnName ||
          presentation.summary.sortDirection !== sortDirection
        ) {
          reportConfigurationActionCreators.setSummarySort({ sortColumnName, sortDirection });
          reportConfigurationEffects.createReportConfigurationAndUpdateUrl();
        }
      }
    };
  }
);
